/**
 * notionSemanticApi — thin client for the integrations engine's semantic
 * Notion read (engines/integrations-engine/src/routes/notion.ts
 * `/notion/import-semantic`).
 *
 * Lives beside the other semanticConnectors modules (like miroExcursionApi)
 * so the surface stays lint-clean and self-contained; it shares the engine
 * base URL via engineUrls. The flat `/notion/import` contract used by
 * EntitySyncPanel is untouched — this is an additive, richer read that also
 * carries relations, page-tree parent, and category color.
 */

import { INTEGRATIONS_ENGINE_URL } from '../engineUrls';

/** A Notion relation property value: the property NAME + the pages it points at. */
export interface NotionRelationRead {
  /** The relation property's name — the cue that resolves to a canonical type. */
  property: string;
  /** External ids of the related pages. */
  targetExternalIds: string[];
}

export interface NotionPageRead {
  externalId: string;
  externalUrl?: string;
  title: string;
  description?: string;
  /** A Notion select/status option color (e.g. 'red'), when the row has one. */
  categoryColor?: string;
  /** The parent PAGE's external id when this page is a sub-page (not a db row). */
  parentPageId?: string;
  relations: NotionRelationRead[];
}

export interface NotionDatabaseRead {
  database: { id: string; title: string; url?: string };
  pages: NotionPageRead[];
}

async function engineRequest<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${INTEGRATIONS_ENGINE_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const payload = (await response.json().catch(() => ({}))) as T & { success?: boolean; error?: string; details?: string };
  if (!response.ok || payload.success === false) {
    throw new Error(payload.error ?? payload.details ?? `Integrations engine error ${response.status}`);
  }
  return payload;
}

/**
 * Read a Notion database as a semantic dialect surface: rows with their
 * relations, page-tree parent, and category color — the data the flat import
 * drops. The caller lifts it into a SemanticSnapshot via buildNotionSnapshot.
 */
export async function notionImportSemantic(
  workspaceId: string,
  databaseId: string,
  opts?: { userId?: string },
): Promise<NotionDatabaseRead> {
  return engineRequest<NotionDatabaseRead>('POST', '/notion/import-semantic', {
    workspaceId,
    databaseId,
    userId: opts?.userId,
  });
}
